import { platformModifierKeyOnly } from 'ol/events/condition';
import { MAC } from 'ol/has';
import MapBrowserEventType from 'ol/MapBrowserEventType';

import { InteractionGroup } from 'cadenza/map/interactions/interaction-group';
import {
  AsyncPointSelectionInteraction
} from 'cadenza/map/interactions/async-point-selection-interaction';
import {
  AsyncRectangleSelectionInteraction
} from 'cadenza/map/interactions/async-rectangle-selection-interaction';
import { getHitObjects } from 'cadenza/api-client/workbook-map-api';
import { parseWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { getDpi } from 'cadenza/map/map-utils';
import { MapProperty } from 'cadenza/map/map-property';
import { supportsTouch } from 'cadenza/utils/media';
import { getWorkbookFromIFrame } from 'cadenza/workbook/workbook-utils';
import { backendSingleExecution } from 'cadenza/api-client/api';
import { logger } from 'cadenza/utils/logging';
import { dataViewIsUsedByTimelinePlayer } from 'cadenza/workbook/view/map/timeline-util';
import { context } from 'cadenza/map/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { isLayerWorkbookDataViewLayer } from 'cadenza/map/layer-utils';
import { workbookMapIframeApi } from 'cadenza/map/custom/workbook/workbook-map-post-message-api';
import { getSettingOrSettingDefault } from 'cadenza/workbook/workbook-settings-util';
import { DATA_VIEW_KEYS } from 'cadenza/workbook/workbook-panel/settings/common-data-view-settings';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';

import i18n from './workbook-selection-interaction.properties';

export class WorkbookSelectionInteraction extends InteractionGroup {

  _map;
  #pointInteraction;
  #rectangleInteraction;
  #alternativeMapInteractionAction;
  _alternativeMapInteractionActionCalled;

  constructor (map, alternativeAction) {
    const singleGetHitObjects = backendSingleExecution(getHitObjects);
    const pointInteraction = new PointSelectionInteraction(map, singleGetHitObjects);
    const rectangleInteraction = new RectangleSelectionInteraction(map, singleGetHitObjects);
    super(pointInteraction, rectangleInteraction);
    this._map = map;
    this.#pointInteraction = pointInteraction;
    this.#rectangleInteraction = rectangleInteraction;
    this.#alternativeMapInteractionAction = alternativeAction;

    this.on('change:active', () => {
      if (!this.getActive() && this._alternativeMapInteractionActionCalled) {
        this.#alternativeMapInteractionAction.interaction.setActive(false);
        this._alternativeMapInteractionActionCalled = false;
      }
    });
  }

  doHandleEvent (event) {
    if (event.type === MapBrowserEventType.SINGLECLICK
      && event.originalEvent.altKey
      && this.#alternativeMapInteractionAction) {
      this._alternativeMapInteractionActionCalled = true;
      return this.#alternativeMapInteractionAction.interaction.handleEvent(event);
    } else if (event.type === MapBrowserEventType.SINGLECLICK
      && this._alternativeMapInteractionActionCalled) {
      this.#alternativeMapInteractionAction.interaction.setActive(false);
      this._alternativeMapInteractionActionCalled = false;
    }

    if (doesMapHaveAnyVisibleDataViewLayers(this._map)) {
      if (event.originalEvent.shiftKey || this.#rectangleInteraction.isInProgress) {
        this.setSingleInteractionActive(this.#rectangleInteraction, true);
        return this.#rectangleInteraction.handleEvent(event);
      }
      if (!event.originalEvent.altKey && this.#alternativeMapInteractionAction) {
        this.setSingleInteractionActive(this.#pointInteraction, true);
        return this.#pointInteraction.handleEvent(event);
      }
    } else {
      return true; // Do not stop the event propagation
    }
  }

}

export function doesMapHaveAnyVisibleDataViewLayers (map) {
  return context(map).layers.some(layer => layer.get(LayerProperty.VISIBLE) && isLayerWorkbookDataViewLayer(layer));
}

class PointSelectionInteraction extends AsyncPointSelectionInteraction {

  #map;
  _singleGetHitObjects;

  constructor (map, singleGetHitObjects) {
    super();
    this.#map = map;
    this._singleGetHitObjects = singleGetHitObjects;
  }

  asyncSelect (mapBrowserEvent, geometry) {
    return getPossibleSelectedObjects(
      mapBrowserEvent.originalEvent,
      this.#map,
      geometry,
      supportsTouch || platformModifierKeyOnly(mapBrowserEvent),
      this._singleGetHitObjects);
  }

}

class RectangleSelectionInteraction extends AsyncRectangleSelectionInteraction {

  #map;

  constructor (map, singleGetHitObjects) {
    super();
    this.#map = map;
    this._singleGetHitObjects = singleGetHitObjects;
  }

  asyncSelect (event, geometry) {
    const originalEvent = event.mapBrowserEvent.originalEvent;
    // platformModifierKeyOnly cannot be used because SHIFT is pressed here
    const isCtrlKeyPressed = !originalEvent.altKey && (MAC ? originalEvent.metaKey : originalEvent.ctrlKey);
    const considerCurrentSelection = supportsTouch || isCtrlKeyPressed;
    return getPossibleSelectedObjects(originalEvent, this.#map, geometry, considerCurrentSelection, this._singleGetHitObjects);
  }

}

function notifyUserAboutChangedSelection (dataView) {
  if (dataView && getSettingOrSettingDefault(dataView.settings, DATA_VIEW_KEYS.COMMON_SELECTION_ONE_CLICK_FILTER_ENABLED)) {
    workbookMapIframeApi.openPanel(WorkbookPanelName.ANALYSIS_CONTEXT);
    workbookMapIframeApi.showToastMessage(i18n('info.filteringApplied'), { type: 'info' });
  }
}

async function getPossibleSelectedObjects (originalEvent, map, geometry, considerCurrentSelection, selectionApiCall) {
  const { workbookIdAndHash, viewId } = parseWorkbookUrl(location.pathname);
  const srsCode = map.getView().getProjection().getCode();
  const mapSize = map.getSize();
  const mapExtent = map.getView().calculateFullExtent();
  const mapScale = context(map).mapScale;
  const dpi = getDpi(map.get(MapProperty.HIDPI));

  try {
    const { dataViewId, selectionElement, definingDimensions, values } = await selectionApiCall({
      workbookIdAndHash,
      viewId,
      geometry,
      srsCode,
      mapExtent,
      mapScale,
      mapSize,
      dpi,
      considerCurrentSelection
    });
    const workbook = getWorkbookFromIFrame();
    const currentSelection = workbook.selection;

    // Records is null when selection cart is empty
    const currentSelectionDataViewId = currentSelection.records?.dataViewId;

    const hasCurrentSelection = !!currentSelection && !!currentSelectionDataViewId;
    const hasNewSelection = !!values.length;
    const isSingleSelection = hasNewSelection && values.length === 1;
    const selectionView = workbook.getViewById(viewId);
    const dataView = selectionView.getDataView(dataViewId);
    const preventSelectionChange = dataViewIsUsedByTimelinePlayer(selectionView, dataViewId); // When timeline player is active, points are rendered client side.

    // No existent selection and no new selection: no need to do anything OR user tries to select
    // points on client side rendered layer (timeline player): not yet implemented. (still allows
    // deselecting points by clicking into void/empty map)
    if ((!hasCurrentSelection && !hasNewSelection) || preventSelectionChange) {
      return;
    }
    const isResettingSelection = hasCurrentSelection && !hasNewSelection;
    const isSimpleSelection = !hasCurrentSelection || !considerCurrentSelection || isResettingSelection; // User made simple click on point or clicked on void/"empty" map and did not press CTRL.

    if (isSimpleSelection && isSingleSelection) {
      const { pageX, pageY } = originalEvent;
      map.set(MapProperty.LAST_SELECTION_CLICK_PX, { pageX, pageY });
    } else {
      map.unset(MapProperty.LAST_SELECTION_CLICK_PX);
    }

    if (isSimpleSelection) { // Select or deselect.
      await workbookMapIframeApi.selection.setSelection({
        dataViewId,
        selectionElement,
        definingDimensions,
        values
      });
      notifyUserAboutChangedSelection(dataView);
    } else if (currentSelectionDataViewId === dataViewId) { // Append new selection to current selection. (user has CTRL + clicked on point)
      await workbookMapIframeApi.selection.invertSelection({
        dataViewId,
        selectionElement,
        definingDimensions,
        values
      });
      notifyUserAboutChangedSelection(dataView);
    } else {
      workbookMapIframeApi.showToastMessage(i18n('warning.incompatible-selection'), { type: 'warning' });
    }
  } catch (error) {
    logger.error('Object selection failed.', error);
  }
}
